import { DataResult, State as GridState, toDataSourceRequestString } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { GridColumn as Column, Grid, GridCustomCellProps, GridDataStateChangeEvent } from "@progress/kendo-react-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { PayStatus } from "../../TypeGen/Pay/pay-status";
import { JsonResponse } from "../../TypeGen/json-response";
import { fetchApi } from "../../services/api";
import { openWindowPerc } from "../../services/openWindow";
import { Title } from "../../utils/title";
import OwnerAutoComplete from "../AssetOwners/OwnerAutoComplete";
import { OwnerViewModel } from "../Assets/OwnerGrid";
import PayPeriodDropDownList, { PayPeriodViewModel } from "./PayPeriodDropDownList";

enum PayDeductionLineItemStatus {
  Pending = 115,
  Posted = 120,
  Cancelled = 130
}

type PayOwnerDiscrepancy = {
  PayID: number;
  TripID: number;
  TripNumber: number;
  PayPeriodID: number;
  OwnerID: number;
  PayOwnerNumber: string;
  DriverID?: number;
  DriverNumber?: string;
  DriverOwnerID?: number;
  DriverOwnerNumber?: string;
  PayStatus: PayStatus;
};

type DeductionOwnerDiscrepancy = {
  PayDeductionLineItemID: number;
  PayDeductionID: number;
  PayPeriodID: number;
  OwnerID: number;
  DeductionOwnerNumber: string;
  DriverID?: number;
  DriverNumber?: string;
  DriverOwnerID?: number;
  DriverOwnerNumber?: string;
  VehicleID?: number;
  VehicleNumber?: string;
  VehicleOwnerID?: number;
  VehicleOwnerNumber?: string;
  PayStatus: PayDeductionLineItemStatus;
};

const OwnerDiscrepancies: React.FC = () => {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search.toLowerCase()), [location.search]);
  const payPeriodIdFromQuery = queryParams.has("payperiodid") ? Number(queryParams.get("payperiodid")) : 0;

  const [payDataState, setPayDataState] = useState<GridState>({ skip: 0, take: 25, sort: [{ field: "PayID", dir: "asc" }] });
  const [deductionDataState, setDeductionDataState] = useState<GridState>({ skip: 0, take: 25, sort: [{ field: "PayDeductionLineItemID", dir: "asc" }] });
  const [payGridData, setPayGridData] = useState<DataResult>({ data: [], total: 0 });
  const [deductionGridData, setDeductionGridData] = useState<DataResult>({ data: [], total: 0 });
  const [payPeriod, setPayPeriod] = useState<PayPeriodViewModel>(payPeriodIdFromQuery > 0 ? { PayPeriodID: payPeriodIdFromQuery } as PayPeriodViewModel : undefined);
  const [payLoading, setPayLoading] = useState(false);
  const [deductionLoading, setDeductionLoading] = useState(false);
  //const [isAdmin, setIsAdmin] = useState(false);
  const [editItem, setEditItem] = useState<DeductionOwnerDiscrepancy>(null);
  const [ownerToUpdate, setOwnerToUpdate] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(null);

  useEffect(() => {
    if (payPeriodIdFromQuery > 0 && payPeriod?.PayPeriodID !== payPeriodIdFromQuery) {
      setPayPeriod({ PayPeriodID: payPeriodIdFromQuery } as PayPeriodViewModel);
    }
  }, [payPeriodIdFromQuery, payPeriod?.PayPeriodID]);

  const refreshPayData = useCallback(() => {
    if (!payPeriod)
      return;

    setPayLoading(true);
    const queryStr = toDataSourceRequestString(payDataState);
    fetchApi(`/api/Settlements/GetPayOwnerDiscrepancies?${queryStr}`, { PayPeriodID: payPeriod.PayPeriodID }, "POST")
      .then((response: any) => {
        setPayGridData({ data: response.Data, total: response.Total });
      })
      .finally(() => setPayLoading(false));
  }, [payDataState, payPeriod]);

  const refreshDeductionData = useCallback(() => {
    if (!payPeriod)
      return;

    setDeductionLoading(true);
    const queryStr = toDataSourceRequestString(deductionDataState);
    fetchApi(`/api/Settlements/GetDeductionOwnerDiscrepancies?${queryStr}`, { PayPeriodID: payPeriod.PayPeriodID }, "POST")
      .then((response: any) => {
        setDeductionGridData({ data: response.Data, total: response.Total });
      })
      .finally(() => setDeductionLoading(false));
  }, [deductionDataState, payPeriod]);

  useEffect(() => {
    refreshPayData();
  }, [payDataState, payPeriod, refreshPayData]);

  useEffect(() => {
    refreshDeductionData();
  }, [deductionDataState, payPeriod, refreshDeductionData]);

  const startEdit = useCallback((item: DeductionOwnerDiscrepancy) => {
    setEditItem(item);
    setOwnerToUpdate(item.OwnerID);
    setErrorMessage(null);
  }, []);

  const updateOwner = useCallback(() => {
    if (!editItem)
      return;

    if (ownerToUpdate <= 0) {
      setErrorMessage("Please select an owner.");
      return;
    }

    setSaving(true);
    fetchApi("/api/Settlements/PayDeductionLineItem/Owner", {
      PayDeductionLineItemID: editItem.PayDeductionLineItemID,
      OwnerID: ownerToUpdate
    }, "POST")
      .then((response: JsonResponse) => {
        if (response.Success) {
          setEditItem(null);
          refreshDeductionData();
        } else {
          setErrorMessage(response.ErrorMessage || "Unable to update owner.");
        }
      })
      .catch(() => setErrorMessage("Unable to update owner."))
      .finally(() => setSaving(false));
  }, [editItem, ownerToUpdate, refreshDeductionData]);

  const closeEditor = useCallback(() => {
    setEditItem(null);
    setOwnerToUpdate(0);
    setErrorMessage(null);
  }, []);

  const statusLookup = useMemo(() => ({
    [PayDeductionLineItemStatus.Pending]: "Pending",
    [PayDeductionLineItemStatus.Posted]: "Posted/Completed",
    [PayDeductionLineItemStatus.Cancelled]: "Cancelled",
  }), []);

  const payStatusLookup = useMemo(() => ({
    [PayStatus.NotRated]: "Not Rated",
    [PayStatus.Rated]: "Rated",
    [PayStatus.ReadyToPrint]: "Ready To Print",
    [PayStatus.ReadyToPost]: "Ready To Post",
    [PayStatus.Posted]: "Posted",
  }), []);

  const UpdateOwnerCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;
    //const disabled = !isAdmin || dataItem.PayStatus !== PayDeductionLineItemStatus.Pending;
    const disabled = dataItem.PayStatus !== PayDeductionLineItemStatus.Pending;
    return <td {...props.tdProps}>
      <Button size="small" onClick={() => startEdit(dataItem)} disabled={disabled}>Update Owner</Button>
    </td>;
  }, [startEdit]);

  const StatusCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;
    return <td {...props.tdProps}>{statusLookup[dataItem.PayStatus] ?? dataItem.PayStatus}</td>;
  }, [statusLookup]);

  const PayStatusCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as PayOwnerDiscrepancy;
    return <td {...props.tdProps}>{payStatusLookup[dataItem.PayStatus] ?? dataItem.PayStatus}</td>;
  }, [payStatusLookup]);

  const PayDeductionIDCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;
    const payDeductionID = dataItem.PayDeductionID;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Settlements/PendingDeductions?PayDeductionID=${payDeductionID}`, .9, .9);
        }}
      >
        {payDeductionID}
      </a>
    </td>;
  }, []);

  const DeductionOwnerNumberCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;

    if (!dataItem.DeductionOwnerNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-warning"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Owner/${dataItem.OwnerID}`);
        }}
      >
        {dataItem.DeductionOwnerNumber}
      </a>
    </td>;
  }, []);

  const PayOwnerNumberCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as PayOwnerDiscrepancy;

    if (!dataItem.PayOwnerNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-warning"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Owner/${dataItem.OwnerID}`);
        }}
      >
        {dataItem.PayOwnerNumber}
      </a>
    </td>;
  }, []);

  const DriverOwnerNumberCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;

    if (!dataItem.DriverOwnerNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Owner/${dataItem.DriverOwnerID}`);
        }}
      >
        {dataItem.DriverOwnerNumber}
      </a>
    </td>;
  }, []);

  const DriverOwnerNumberCellPay = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as PayOwnerDiscrepancy;

    if (!dataItem.DriverOwnerNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Owner/${dataItem.DriverOwnerID}`);
        }}
      >
        {dataItem.DriverOwnerNumber}
      </a>
    </td>;
  }, []);

  const VehicleOwnerNumberCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;

    if (!dataItem.VehicleOwnerNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Owner/${dataItem.VehicleOwnerID}`);
        }}
      >
        {dataItem.VehicleOwnerNumber}
      </a>
    </td>;
  }, []);

  const DriverNumberCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;

    if (!dataItem.DriverNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Driver/${dataItem.DriverID}`);
        }}
      >
        {dataItem.DriverNumber}
      </a>
    </td>;
  }, []);

  const DriverNumberCellPay = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as PayOwnerDiscrepancy;

    if (!dataItem.DriverNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Driver/${dataItem.DriverID}`);
        }}
      >
        {dataItem.DriverNumber}
      </a>
    </td>;
  }, []);

  const TripNumberCellPay = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as PayOwnerDiscrepancy;

    if (!dataItem.DriverNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Trip/${dataItem.TripID}`);
        }}
      >
        {dataItem.TripNumber}
      </a>
    </td>;
  }, []);

  const VehicleNumberCell = useCallback((props: GridCustomCellProps) => {
    const dataItem = props.dataItem as DeductionOwnerDiscrepancy;

    if (!dataItem.VehicleNumber)
      return <td {...props.tdProps}>-</td>;

    return <td {...props.tdProps}>
      <a
        className="text-primary"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          openWindowPerc(`/Assets/Vehicle/${dataItem.VehicleID}`);
        }}
      >
        {dataItem.VehicleNumber}
      </a>
    </td>;
  }, []);

  const onPayDataStateChange = (event: GridDataStateChangeEvent) => {
    setPayDataState(event.dataState);
  };

  const onDeductionDataStateChange = (event: GridDataStateChangeEvent) => {
    setDeductionDataState(event.dataState);
  };

  return <div>
    <Title string="Owner Discrepancies" />
    <h2 className="mt-3">Owner Discrepancies</h2>
    <div className="row mb-3">
      <div className="col-md-4">
        <label>Pay Period</label>
        <div>
          <PayPeriodDropDownList
            includeClosedPayPeriods={false}
            includeOpenPayPeriods={true}
            defaultPayPeriodItem={{ PayPeriodID: 0, Label: "Select Pay Period" }}
            onSelected={(payPeriod) => setPayPeriod(payPeriod)}
            selectedPayPeriodID={payPeriod?.PayPeriodID}
            defaultToFirstOpenPayPeriod
          />
        </div>
      </div>
    </div>

    <h3 className="mt-4">Pay Owner Discrepancies</h3>
    <Grid
      style={{ maxHeight: "40vh" }}
      data={payGridData.data}
      pageable
      sortable
      total={payGridData.total}
      {...payDataState}
      onDataStateChange={onPayDataStateChange}
      showLoader={payLoading}
    >
      <Column field="TripNumber" title="Trip Number" cells={{ data: TripNumberCellPay }} />
      <Column field="DriverNumber" title="Driver" cells={{ data: DriverNumberCellPay }} />
      <Column field="DriverOwnerNumber" title="Current Driver Owner" cells={{ data: DriverOwnerNumberCellPay }} />
      <Column field="PayOwnerNumber" title="Pay Owner" cells={{ data: PayOwnerNumberCell }} />
      <Column field="PayStatus" title="Status" cells={{ data: PayStatusCell }} />
    </Grid>

    <h3 className="mt-4">Deduction Owner Discrepancies</h3>
    <Grid
      style={{ maxHeight: "75vh" }}
      data={deductionGridData.data}
      pageable
      sortable
      total={deductionGridData.total}
      {...deductionDataState}
      onDataStateChange={onDeductionDataStateChange}
      showLoader={deductionLoading}
    >
      <Column field="PayDeductionLineItemID" title="PDLID" />
      <Column field="PayDeductionID" title="PDID" cells={{ data: PayDeductionIDCell }} />
      <Column field="DriverNumber" title="Driver" cells={{ data: DriverNumberCell }} />
      <Column field="DriverOwnerNumber" title="Current Driver Owner" cells={{ data: DriverOwnerNumberCell }} />
      <Column field="VehicleNumber" title="Vehicle" cells={{ data: VehicleNumberCell }} />
      <Column field="VehicleOwnerNumber" title="Current Vehicle Owner" cells={{ data: VehicleOwnerNumberCell }} />
      <Column field="DeductionOwnerNumber" title="Deduction Owner" cells={{ data: DeductionOwnerNumberCell }} />
      <Column field="PayStatus" title="Status" cells={{ data: StatusCell }} />
      <Column title="Actions" cells={{ data: UpdateOwnerCell }} />
    </Grid>

    {editItem && <Window title="Update Deduction Owner" onClose={closeEditor} style={{ width: 500 }}>
      <div className="mb-3">
        <strong>Pay Deduction Line Item ID:</strong> {editItem.PayDeductionLineItemID}
      </div>
      <div>
        <strong>Deduction Owner:</strong> {editItem.DeductionOwnerNumber}
      </div>
      {editItem.DriverOwnerNumber && <div className="mt-1">
        <strong>Current Driver Owner:</strong> {editItem.DriverOwnerNumber}
      </div>}
      {editItem.VehicleOwnerNumber && <div className="mt-1">
        <strong>Current Vehicle Owner:</strong> {editItem.VehicleOwnerNumber}
      </div>}
      <div className="mt-3 mb-3">
        <label>New Owner</label>
        <OwnerAutoComplete
          onSelectedOwner={(owner: OwnerViewModel) => setOwnerToUpdate(owner?.OwnerID ?? 0)}
          selectedOwnerID={ownerToUpdate}
        />
      </div>
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
      <div className="text-right">
        <Button fillMode="outline" className="mr-2" onClick={closeEditor}>Cancel</Button>
        <Button themeColor="primary" onClick={updateOwner} disabled={saving || ownerToUpdate <= 0}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </Window>}
  </div>;
};

export default OwnerDiscrepancies;
